import { useState } from 'react'
import toast from 'react-hot-toast'
import {fetchWithAuth} from '../../libs/apiUtils'
import Modal from '../Modal'
import {Option} from '../../types/global'
import {DocumentDuplicateIcon as DocumentDuplicateIconOutline, CheckIcon} from '@heroicons/react/24/outline'

interface AddUserModalProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  roleOptions: Option[]
  onSuccess: () => void
}

interface AddUserRequest {
  roleId: string
  fullName: string
  email: string
}

interface AddUserResponse {
  id: string
  fullName: string
  email: string
  role: string
  password: string
}

const defaultValue: AddUserRequest = {
  roleId: '',
  fullName: '',
  email: ''
}

const defaultResponse: AddUserResponse = {
  id: '',
  fullName: '',
  email: '',
  role: '',
  password: ''
}

export default function AddUserModal({ 
  isOpen, 
  setIsOpen, 
  roleOptions, 
  onSuccess 
}: AddUserModalProps) {
  const [request, setRequest] = useState<AddUserRequest>(defaultValue)
  const [response, setResponse] = useState<AddUserResponse>(defaultResponse)
  const [loading, setLoading] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [isPasswordCopied, setIsPasswordCopied] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!request.fullName.trim()) return toast.error('Nama lengkap tidak boleh kosong')
    if (!request.email.trim()) return toast.error('Email tidak boleh kosong')
    if (!request.roleId.trim()) return toast.error('Pilih role user')

    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/user-management`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data[0].message || 'Error saat menambahkan user')
        return
      }

      setResponse({
        id: data.data.id,
        fullName: data.data.fullName,
        email: data.data.email,
        role: data.data.role,
        password: data.data.password
      })

      toast.success('User berhasil ditambahkan')
      setIsSaved(true)
      onSuccess()
    } catch (err) {
      toast.error('Kesalahan jaringan, periksa koneksi Anda')
    } finally {
      setLoading(false)
    }
  }

  function handleClose() {
    setRequest(defaultValue)
    setResponse(defaultResponse)
    setIsSaved(false)
    setIsOpen(false)
  }

  function handleCopyPassword() {
    if (!response.password) return
    
    navigator.clipboard.writeText(response.password)
      .then(() => {
        setIsPasswordCopied(true)
        setTimeout(() => setIsPasswordCopied(false), 2000)
      })
      .catch(() => {
        toast.error('Gagal menyalin password')
      })
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <h3 className="text-xl font-bold text-neutral-800 mb-4">Tambah User Baru</h3>
      
      {isSaved && (
        <div className="mb-6 p-4 bg-primary-50 border border-primary-100 rounded-xl">
          <p className="text-sm text-primary-700 font-medium mb-1 italic">
            Catat password di bawah ini. Password hanya akan ditampilkan sekali.
          </p>
        </div>
      )}

      <form className='flex flex-col gap-4' onSubmit={handleSubmit}>
        <div>
          <label htmlFor="fullName" className="block text-sm font-semibold text-neutral-600 mb-2">
            Nama Lengkap
          </label>
          <input
            type="text"
            id="fullName"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none disabled:bg-neutral-50 disabled:text-neutral-500"
            value={request.fullName}
            onChange={(e) => setRequest((prev) => ({...prev, fullName: e.target.value}))}
            disabled={isSaved}
            placeholder="Contoh: John Doe"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-neutral-600 mb-2">
            Alamat Email
          </label>
          <input
            type="email"
            id="email"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none disabled:bg-neutral-50 disabled:text-neutral-500"
            value={request.email}
            onChange={(e) => setRequest((prev) => ({...prev, email: e.target.value}))}
            disabled={isSaved}
            placeholder="john@example.com"
          />
        </div>

        <div>
          <label htmlFor="roleId" className="block text-sm font-semibold text-neutral-600 mb-2">
            Role
          </label>
          <select
            id="roleId"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none bg-white disabled:bg-neutral-50 disabled:text-neutral-500"
            value={request.roleId}
            onChange={(e) => setRequest((prev) => ({...prev, roleId: e.target.value}))}
            disabled={isSaved}
          >
            <option value="">Pilih role</option>
            {roleOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.value}
              </option>
            ))}
          </select>
        </div>

        { isSaved ? (
          <div className="mt-2">
            <label htmlFor="password" className="block text-sm font-semibold text-neutral-600 mb-2">
              Password Sementara
            </label>
            <div className="flex gap-2">
              <input
                id="password"
                className="flex-1 border border-neutral-300 px-4 py-2 rounded-lg bg-neutral-50 font-mono text-neutral-700"
                value={response.password}
                readOnly
              />
              <button 
                type='button' 
                onClick={handleCopyPassword}
                className={`p-2 rounded-lg border transition-all ${isPasswordCopied ? 'bg-green-50 border-green-200 text-green-600' : 'bg-white border-neutral-300 text-neutral-500 hover:bg-neutral-50'}`}
                title="Salin Password"
              >
                { isPasswordCopied ? <CheckIcon className='size-6' /> : <DocumentDuplicateIconOutline className='size-6' /> }
              </button>
            </div>
            <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  className="bg-primary-600 text-white px-8 py-2 rounded-lg font-semibold hover:bg-primary-700 active:scale-95 transition-all shadow-lg shadow-primary-200"
                  onClick={handleClose}
                >
                  Selesai
                </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              className="px-4 py-2 text-neutral-600 font-medium rounded-lg hover:bg-neutral-100 transition-colors"
              onClick={handleClose}
              disabled={loading}
            >
              Batal
            </button>
            <button
              type="submit"
              className="bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 active:scale-95 disabled:opacity-50 disabled:active:scale-100 transition-all"
              disabled={loading}
            >
              {loading ? 'Menyimpan...' : 'Simpan User'}
            </button>
          </div>
        )}
      </form>
    </Modal>
  )
}
